import React from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

const FAQ = () => {
  const theme = useTheme();
  const faqs = [
    {
      question: 'How Do I Use Sweetums Wipes?',
      answer: 'Remove wipe from pouch and gently cleanse your intimate parts from front to back. Discard after each use. The wipe itself is not edible. Do not apply to broken skin. Carefully crafted with your intimate parts in mind. If for some reason, irritation occurs, stop use immediately.'
    },
    {
      question: 'How Do I Use Sweetums Sweet Spot Flavored Fragrance Stick?',
      answer: `Glide a Sweet Spot flavored fragrance stick across your body…your neck, wrists, or wherever it takes you. You'll not only smell irresistible, you'll also taste ahh-may-zing.`
    },
    {
      question: 'What Are The Ingredients?',
      answer: (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Flavored Women's Wipes:</Typography>
          <Typography paragraph>
            Water, Glycerin, Stevia Rebaudiana Extract, Xanthan Gum, Citric Acid, Sodium Benzoate, Potassium Sorbate, Flavor
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Flavored Men's Wipes:</Typography>
          <Typography paragraph>
            Water, Glycerin, Propylene Glycol, Rebaudioside A, Xanthan Gum, Citric Acid, Sodium Benzoate, Potassium Sorbate, Flavor
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Unflavored Wipes:</Typography>
          <Typography paragraph>
            Water, Glycerin, Xanthan Gum, Maltodextrin, Citric Acid, Silicon Dioxide, Sodium Benzoate, Potassium Sorbaten, Natural Fragrance
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Flavored Fragrance:</Typography>
          <Typography>
            Corn Starch, Synthetic Beeswax, Alkyl Benzoate, Isoeicosane, Flavor, Hydrogenated Castor Oil, Alkyl Fumarate, Silica, Polyricinoleic Acid, Caprylyl Glycol, Castor Seed Oil, Stevia
          </Typography>
        </>
      )
    },
    {
      question: `How Do I Discard Of Sweetums Wipes Once They've Been Used?`,
      answer: 'Please dispose responsibly. Do not flush.'
    },
    {
      question: 'Can Sweetums Wipes Be Used On Other Parts Of The Body?',
      answer: 'We encourage you to get creative with your Sweetums Wipes. Lips, necks, thighs and other body parts are ready to be explored. For external use only.'
    },
    {
      question: 'Are Sweetums Cruelty-free?',
      answer: 'We do not conduct or commission any animal tests on ingredients, formulations, or finished products and pledge never to do so.'
    },
    {
      question: 'Will Sweetums Wipes Prevent Me From Contracting An STD?',
      answer: 'No, silly. It will not.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: theme.palette.primary.main,
          color: 'white',
          py: { xs: 6, md: 8 },
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4))',
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/faq-hero.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scale(1.1)',
            transition: 'transform 0.3s ease-out',
            '&:hover': {
              transform: 'scale(1.15)',
            },
          }}
        />
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              maxWidth: 1000,
              mx: 'auto',
              px: 3,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                color: 'white',
                letterSpacing: '-0.02em',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                lineHeight: 1.6,
                maxWidth: 800,
                mx: 'auto',
                fontWeight: 'normal',
                textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                color: 'white',
                fontSize: { xs: '1.1rem', md: '1.3rem' },
              }}
            >
              Find answers to common questions about our products and services
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Box sx={{ mt: 4 }}>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                mb: 2,
                '&:before': { display: 'none' },
                boxShadow: theme.shadows[1],
                borderRadius: '12px !important',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[3],
                },
                '&.Mui-expanded': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[3],
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon 
                    sx={{ 
                      color: theme.palette.primary.main,
                      transition: 'transform 0.3s ease',
                      transform: 'rotate(0deg)',
                      '.Mui-expanded &': {
                        transform: 'rotate(-180deg)',
                      },
                    }} 
                  />
                }
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                  '&.Mui-expanded': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <QuestionAnswerIcon 
                    sx={{ 
                      color: theme.palette.primary.main,
                      opacity: 0.7,
                    }} 
                  />
                  <Typography 
                    sx={{ 
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: '1.1rem',
                    }}
                  >
                    {faq.question}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: '#fff',
                  p: 3,
                  '& .MuiTypography-root': {
                    color: theme.palette.text.secondary,
                    lineHeight: 1.7,
                  },
                }}
              >
                {faq.answer}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FAQ;
